import React from 'react';
import { CoreTextfield } from '@komodo-design-system/komodo-components-react';
import { MetadataElement, MetadataElementList } from './SingleUploadModel';
import { useDispatch, useSelector } from 'react-redux';
import {
  NON_EDITABLE_TABLE_FIELDS,
  SINGLE_UPLOAD_SEARCHABLE_FIELD_ID
} from 'constants/constants';
import './SingleUploadContent.scss';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { singleUploadActions } from 'actions/singleUpload';
import AlertBox from 'componentsV2/SharedAlertBox/AlertBox/AlertBox';
import { inputValidationType } from 'type/productValidation';
import inputValidation from 'actions/inputValidation';
import { domParser } from 'utils/utils';

interface TextFieldProps {
  element: MetadataElement;
  rowIndex?: number | null; // Added rowIndex prop
}

const TextFieldWrapper: React.FC<TextFieldProps> = ({ element, rowIndex }) => {
  const [alert, setAlert] = React.useState({ message: '', isOpen: false });
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const singleUploadPayload = useSelector(
    (state: any) => state.singleUpload.payload
  );
  const { eventType } = useSelector(
    (state: any) => state.inputValidationReducer
  );
  const { eventType: submitEventType } = useSelector(
    (state: any) => state.submitValidationReducer
  );
  // for prouduct identifiers
  const isProductIdentifiers =
    singleUploadPayload?.metadata_collection?.metadata_element_list?.findIndex(
      (obj: any) => obj.id === 'TFS.CATEGORY.PRODUCT_IDENTIFIERS'
    ) > -1;
  const checkboxValues = isProductIdentifiers && singleUploadPayload?.metadata_collection?.metadata_element_list
    ?.find((obj: any) => obj.id === 'TFS.CATEGORY.PRODUCT_IDENTIFIERS')
    ?.metadata_element_list?.find(
      (obj: any) => obj.field_id === 'TFS.FIELD.IS_CUSTOM'
    )?.values;
  const isCustomField = checkboxValues[rowIndex as number]?.id;

  // Utility function to update the Redux store
  const updateReduxStore = (updatedPayload: MetadataElementList[]) => {
    const updatedSingleUploadPayload = {
      ...singleUploadPayload,
      metadata_collection: {
        ...singleUploadPayload.metadata_collection,
        metadata_element_list: updatedPayload
      }
    };
    dispatch(
      singleUploadActions.updateSingleUploadPayload(updatedSingleUploadPayload)
    );
  };

  // Handle text field changes
  const handleTextfieldChange = (value: string) => {
    const updatedPayload = singleUploadPayload?.metadata_collection?.metadata_element_list?.map(
      (el: MetadataElementList) => {
        if (el.name === element.name) {
          el.metadata_element_list.forEach((field: MetadataElement) => {
            if (field.field_id === element.field_id) {
              if (
                field.values &&
                Array.isArray(field.values) &&
                field.values.length > 0
              ) {
                field.values[rowIndex || 0] = {
                  id: value,
                  display_value: value
                };
              }
              if (field.value && typeof field.value === 'object') {
                field.value = { id: value, display_value: value };
              }
            }
          });
        }
        return el;
      }
    );
    updateReduxStore(updatedPayload);
  };

  // Validate PubMed ID
  const pubmedIdValidation = async (
    apiValue: string | undefined,
    value: string
  ) => {
    dispatch({ type: 'LOADING SUCCESS', payload: true });

    // Clear existing values under Citation Identifiers
    const clearedPayload = singleUploadPayload?.metadata_collection?.metadata_element_list?.map(
      (el: MetadataElementList) => {
        if (['Citation Identifiers', 'Article'].includes(el.name)) {
          el.metadata_element_list.forEach((field: MetadataElement) => {
            if (field.field_id !== 'TFS.FIELD.PMID') {
              field.value = { id: '', display_value: '' };
            }
          });
        }
        return el;
      }
    );
    updateReduxStore(clearedPayload);

    // Call the API to validate PubMed ID
    const response = await singleUploadActions.pubmedIdValidation(value);
    dispatch({ type: 'LOADING SUCCESS', payload: false });

    if (
      !response ||
      response.response_code !== 'SUCCESS' ||
      response?.message ||
      response?.error
    ) {
      setAlert({
        message: response?.message || 'Unable to validate PubMed ID',
        isOpen: true
      });
      return;
    }

    // Update payload with API response
    const updatedPayload = singleUploadPayload?.metadata_collection?.metadata_element_list?.map(
      (el: MetadataElementList) => {
        el.metadata_element_list.forEach((field: MetadataElement) => {
          const key = Object.keys(response).find(
            (k) => `TFS.FIELD.${k.toUpperCase()}` === field.field_id
          );
          if (key) {
            const fieldValue =
              response[key as keyof typeof response]?.toString() || '';
            field.value = { id: fieldValue, display_value: fieldValue };
          } else if (
            field.field_id === 'TFS.FIELD.EXTERNAL_LINK' &&
            response.pmid
          ) {
            const fieldValue = `https://pubmed.ncbi.nlm.nih.gov/${response.pmid}`;
            field.value = { id: fieldValue, display_value: fieldValue };
          } else if (field.field_id === 'TFS.FIELD.CIT_PAGINATION') {
            const fieldValue = response.pages?.toString() || '';
            field.value = { id: fieldValue, display_value: fieldValue };
          }
        });
        return el;
      }
    );
    updateReduxStore(updatedPayload);
  };

  // Handle text field blur
  const handleTextfieldBlur = async (value: string) => {
    if (!value || eventType) {
      return;
    }

    const apiValue = SINGLE_UPLOAD_SEARCHABLE_FIELD_ID.find(
      (field) => field.id === element.field_id
    )?.api;
    if (
      (isCustomField === 'N' || !isCustomField || isCustomField === 'False') &&
      (element.field_id === 'TFS.FIELD.WEB_ID' ||
        element.field_id === 'TFS.FIELD.MPN' ||
        element.field_id === 'TFS.FIELD.ROOT_SKU')
    ) {
      dispatch(
        inputValidation({
          eventType: 'blur',
          fieldId: element.field_id,
          value: value,
          rowIndex
        } as inputValidationType)
      );
    } else if (element.field_id === 'TFS.FIELD.PMID') {
      await pubmedIdValidation(apiValue, value);
    }
  };

  // Get the selected value from Redux state
  let selectedValue: any = null; // Initialize selectedValue
  if (element?.values?.length ?? 0 > 1) {
    selectedValue = singleUploadPayload?.metadata_collection?.metadata_element_list
      ?.find((el: MetadataElement) => el.name === element.name)
      ?.metadata_element_list.find(
        (el: MetadataElement) => el.field_id === element.field_id
      )?.values[rowIndex as number]?.display_value;
  } else {
    selectedValue = singleUploadPayload?.metadata_collection?.metadata_element_list
      ?.find((el: MetadataElement) => el.name === element.name)
      ?.metadata_element_list.find(
        (el: MetadataElement) => el.field_id === element.field_id
      )?.value?.display_value;
  }

  // error message
  const getErrorMessage = () => {
    if (
      submitEventType === 'validate' &&
      !selectedValue &&
      element.is_required === 'Y'
    ) {
      return 'This field is required.';
    }
    if (
      selectedValue?.length === element.data_length &&
      element.data_length > 0
    ) {
      return `Maximum characters allowed ${element.data_length}.`;
    }
    return '';
  };

  const getReadOnly = () => {
    if (element.field_id === 'TFS.FIELD.PRODUCT_GLOBAL_IDENTIFIER_GID') {
      return true; // GID is read-only
    } else if (isProductIdentifiers && isCustomField === 'Y') {
      return false;
    } else if (NON_EDITABLE_TABLE_FIELDS.includes(element.field_id)) {
      return true; // Non-editable fields
    } else if (element.is_editable === 'Y') {
      return false;
    }

    return true;
  };

  return (
    <>
      <AlertBox
        isModalOpen={alert.isOpen}
        message={alert.message}
        handleClose={() => setAlert({ ...alert, isOpen: false })}
      />
      {getReadOnly() ? (
        <div>{domParser(selectedValue) || ''}</div>
      ) : (
        <CoreTextfield
          maxlength={
            element.data_length && element.data_length > 0
              ? element.data_length
              : undefined
          }
          type={element.datatype === 'TEXT' ? 'textarea' : 'text'}
          title={element.prompt}
          placeholder={`${element.prompt}`}
          value={selectedValue}
          onTextfieldChange={(event) =>
            handleTextfieldChange(event.detail.trim())
          }
          onTextfieldFocusOut={
            SINGLE_UPLOAD_SEARCHABLE_FIELD_ID.some(
              (field) => field.id === element.field_id
            )
              ? (event) => handleTextfieldBlur(event.detail.trim())
              : undefined
          }
          helper={getErrorMessage()}
          invalid={getErrorMessage() !== ''}
        />
      )}
    </>
  );
};

export default TextFieldWrapper;
